import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  IconButton,
  Button,
  Popover,
  Radio,
  RadioGroup,
  FormControlLabel,
  Typography,
  useTheme,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import DatePicker from "../../../../components/DatePicker/DatePicker";

const JoinDateFilterPopover = ({ onFilterChange, activeFilter }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterDate, setFilterDate] = useState(activeFilter?.date || "");
  const [filterMode, setFilterMode] = useState(activeFilter?.mode || null);

  const open = Boolean(anchorEl);
  const isFilterActive =
    activeFilter?.mode === "between_audit_period"
      ? true
      : activeFilter?.date && activeFilter?.mode;

  // Sync local state with activeFilter prop
  useEffect(() => {
    setFilterDate(activeFilter?.date || "");
    setFilterMode(activeFilter?.mode || null);
  }, [activeFilter]);

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const handleApplyFilter = () => {
    if (filterMode === "between_audit_period") {
      onFilterChange({ date: "", mode: "between_audit_period" });
    } else if (filterDate && filterMode) {
      onFilterChange({ date: filterDate, mode: filterMode });
    }
    handleFilterClose();
  };

  const handleResetFilter = () => {
    setFilterDate("");
    setFilterMode(null);
    onFilterChange({ date: "", mode: null });
    handleFilterClose();
  };

  const componentStyles = {
    popover: {
      width: "240px",
      maxHeight: "300px",
      borderRadius: theme.spacing(1),
      boxShadow: theme.shadows[4],
      border: `1px solid ${theme.palette.divider}`,
      overflow: "hidden",
      backgroundColor: theme.palette.background.paper,
    },
    header: {
      padding: `${theme.spacing(1)} ${theme.spacing(1.5)} ${theme.spacing(0.75)} ${theme.spacing(1.5)}`,
      backgroundColor: theme.palette.grey[50],
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    dateInput: {
      "& .MuiOutlinedInput-root": {
        height: "32px",
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.spacing(0.75),
        fontSize: "12px",
        "& fieldset": {
          borderColor: theme.palette.grey[300],
        },
        "&:hover fieldset": {
          borderColor: theme.palette.primary.main,
        },
        "&.Mui-focused fieldset": {
          borderColor: theme.palette.primary.main,
          borderWidth: "1px",
        },
      },
      "& .MuiInputLabel-root": {
        fontSize: "12px",
        transform: "translate(14px, 8px) scale(1)",
        "&.MuiInputLabel-shrink": {
          transform: "translate(14px, -6px) scale(0.75)",
        },
      },
    },
    content: {
      padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
      backgroundColor: theme.palette.background.paper,
    },
    radioGroup: {
      gap: theme.spacing(0.25),
    },
    radioLabel: {
      margin: 0,
      padding: `${theme.spacing(0.25)} 0`,
      "& .MuiFormControlLabel-label": {
        fontSize: "12px",
        color: theme.palette.text.secondary,
        fontWeight: 400,
      },
    },
    radio: {
      padding: theme.spacing(0.25),
      marginRight: theme.spacing(0.75),
      color: theme.palette.grey[600],
      "&.Mui-checked": {
        color: theme.palette.primary.main,
      },
    },
    footer: {
      padding: `${theme.spacing(0.75)} ${theme.spacing(1.5)} ${theme.spacing(1)} ${theme.spacing(1.5)}`,
      backgroundColor: theme.palette.grey[50],
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    buttonContainer: {
      display: "flex",
      gap: theme.spacing(0.75),
    },
    resetButton: {
      flex: 1,
      fontSize: "11px",
      fontWeight: 500,
      textTransform: "none",
      borderRadius: theme.spacing(0.5),
      minWidth: "auto",
      px: theme.spacing(1.5),
    },
    applyButton: {
      flex: 1,
      fontSize: "11px",
      fontWeight: 600,
      textTransform: "none",
      borderRadius: theme.spacing(0.5),
      backgroundColor: theme.palette.primary.main,
      minWidth: "auto",
      px: theme.spacing(1.5),
      "&:hover": {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={handleFilterClick}
        sx={{
          ml: 0.5,
          padding: "4px",
          backgroundColor: isFilterActive
            ? theme.palette.grey[200]
            : "transparent",
          borderRadius: theme.spacing(1),
          "&:hover": {
            backgroundColor: isFilterActive
              ? theme.palette.grey[300]
              : theme.palette.grey[100],
          },
        }}
      >
        <FilterListIcon fontSize="small" />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: componentStyles.popover }}
      >
        {/* HEADER */}
        <Box sx={componentStyles.header}>
          <DatePicker
            size="small"
            value={filterDate}
            onChange={(value) => {
              setFilterDate(value);
              setFilterMode((prevMode) =>
                value
                  ? prevMode === "between_audit_period"
                    ? null
                    : prevMode
                  : prevMode
              );
            }}
            InputLabelProps={{ shrink: true }}
            
            sx={componentStyles.dateInput}
          />
        </Box>

        {/* CONTENT */}
        <Box sx={componentStyles.content}>
          <RadioGroup
            value={filterMode}
            onChange={(e) => setFilterMode(e.target.value)}
            sx={componentStyles.radioGroup}
          >
            {/* Custom date section */}
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                fontSize: "11px",
                textTransform: "uppercase",
                color: theme.palette.text.secondary,
                mb: 0.5,
              }}
            >
              Custom date
            </Typography>

            <FormControlLabel
              value="before"
              control={
                <Radio
                  size="small"
                  disabled={!filterDate}
                  sx={componentStyles.radio}
                />
              }
              label="Joined before selected date"
              sx={componentStyles.radioLabel}
            />
            <FormControlLabel
              value="after"
              control={
                <Radio
                  size="small"
                  disabled={!filterDate}
                  sx={componentStyles.radio}
                />
              }
              label="Joined after selected date"
              sx={componentStyles.radioLabel}
            />

            {/* Audit window section */}
            <Box
              sx={{
                mt: 1.5,
                pt: 1,
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  fontSize: "11px",
                  textTransform: "uppercase",
                  color: theme.palette.text.secondary,
                  mb: 0.25,
                }}
              >
                Audit window
              </Typography>

              <FormControlLabel
                value="between_audit_period"
                control={
                  <Radio
                    size="small"
                    disabled={!!filterDate}
                    sx={componentStyles.radio}
                  />
                }
                label="Joined within audit period"
                sx={componentStyles.radioLabel}
              />
            </Box>
          </RadioGroup>
        </Box>

        {/* FOOTER */}
        <Box sx={componentStyles.footer}>
          <Box sx={componentStyles.buttonContainer}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleResetFilter}
              sx={componentStyles.resetButton}
            >
              Reset
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleApplyFilter}
              disabled={
                (!filterDate || !filterMode) &&
                filterMode !== "between_audit_period"
              }
              sx={componentStyles.applyButton}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default JoinDateFilterPopover;
